import React from 'react';
import PropTypes from 'prop-types';
import anime from 'animejs';

import Headline from 'src/components/Common/Headline';
import Icon from 'src/components/Common/Icon';
import Link from 'src/components/Common/Link';
import Picture from 'src/components/Common/Picture';
import ReactEvent from 'src/components/ReactEvent';

// ----------------------------------------------------------------------------
/**
 * main.js
 * http://www.codrops.com
 *
 * Licensed under the MIT license.
 * http://www.opensource.org/licenses/mit-license.php
 *
 * Copyright 2016, Codrops
 * http://www.codrops.com
 */

// Helper vars and functions.
function extend(a, b) {
  for (const key in b) {
    if (b.hasOwnProperty(key)) {
      a[key] = b[key];
    }
  }
  return a;
}

function createDOMEl(type, className, content) {
  const el = document.createElement(type);
  el.className = className || '';
  el.innerHTML = content || '';
  return el;
}

/**
 * RevealFx obj.
 */
function RevealFx(el, options) {
  this.el = el;
  this.options = extend({}, this.options);
  extend(this.options, options);
  this._init();
}

/**
 * RevealFx options.
 */
RevealFx.prototype.options = {
  // If true, then the content will be hidden until it´s "revealed".
  isContentHidden: true,
  // The animation/reveal settings. This can be set initially or passed when calling the reveal method.
  revealSettings: {
    // Animation direction: left right (lr) || right left (rl) || top bottom (tb) || bottom top (bt).
    direction: 'lr',
    // Revealer´s background color.
    bgcolor: '#f0f0f0',
    // Animation speed. This is the speed to "cover" and also "uncover" the element (seperately, not the total time).
    duration: 500,
    // Animation easing. This is the easing to "cover" and also "uncover" the element.
    easing: 'easeInOutQuint',
    // percentage-based value representing how much of the area should be left covered.
    coverArea: 0,
    // Callback for when the revealer is covering the element (halfway through of the whole animation).
    onCover(contentEl, revealerEl) {
      return false;
    },
    // Callback for when the animation starts (animation start).
    onStart(contentEl, revealerEl) {
      return false;
    },
    // Callback for when the revealer has completed uncovering (animation end).
    onComplete(contentEl, revealerEl) {
      return false;
    },
  },
};

/**
 * Init.
 */
RevealFx.prototype._init = function () {
  this._layout();
};

/**
 * Build the necessary structure.
 */
RevealFx.prototype._layout = function () {
  const { position } = getComputedStyle(this.el);
  if (
    position !== 'fixed' &&
    position !== 'absolute' &&
    position !== 'relative'
  ) {
    this.el.style.position = 'relative';
  }
  // Content element.
  this.content = createDOMEl(
    'div',
    'block-revealer__content',
    this.el.innerHTML,
  );
  if (this.options.isContentHidden) {
    this.content.style.opacity = 0;
  }
  // Revealer element (the one that animates)
  this.revealer = createDOMEl('div', 'block-revealer__element');
  this.el.classList.add('block-revealer');
  this.el.innerHTML = '';
  this.el.appendChild(this.content);
  this.el.appendChild(this.revealer);
};

/**
 * Gets the revealer element´s transform and transform origin.
 */
RevealFx.prototype._getTransformSettings = function (direction) {
  let val;
  let origin;
  let origin_2;

  switch (direction) {
    case 'lr':
      val = 'scale3d(0,1,1)';
      origin = '0 50%';
      origin_2 = '100% 50%';
      break;
    case 'rl':
      val = 'scale3d(0,1,1)';
      origin = '100% 50%';
      origin_2 = '0 50%';
      break;
    case 'tb':
      val = 'scale3d(1,0,1)';
      origin = '50% 0';
      origin_2 = '50% 100%';
      break;
    case 'bt':
      val = 'scale3d(1,0,1)';
      origin = '50% 100%';
      origin_2 = '50% 0';
      break;
    default:
      val = 'scale3d(0,1,1)';
      origin = '0 50%';
      origin_2 = '100% 50%';
      break;
  }

  return {
    // transform value.
    val,
    // initial and halfway/final transform origin.
    origin: { initial: origin, halfway: origin_2 },
  };
};

/**
 * Reveal animation. If revealSettings is passed, then it will overwrite the options.revealSettings.
 */
RevealFx.prototype.reveal = function (revealSettings) {
  // Do nothing if currently animating.
  if (this.isAnimating) {
    return false;
  }
  this.isAnimating = true;

  // Set the revealer element´s transform and transform origin.
  const defaults = {
    // In case revealSettings is incomplete, its properties deafault to:
    duration: 500,
    easing: 'easeInOutQuint',
    delay: 0,
    bgcolor: '#f0f0f0',
    direction: 'lr',
    coverArea: 0,
  };
  var revealSettings = revealSettings || this.options.revealSettings;
  const direction = revealSettings.direction || defaults.direction;
  const transformSettings = this._getTransformSettings(direction);

  this.revealer.style.WebkitTransform = this.revealer.style.transform =
    transformSettings.val;
  this.revealer.style.WebkitTransformOrigin =
    this.revealer.style.transformOrigin = transformSettings.origin.initial;

  // Set the Revealer´s background color.
  this.revealer.style.backgroundColor =
    revealSettings.bgcolor || defaults.bgcolor;

  // Show it. By default the revealer element has opacity = 0 (CSS).
  this.revealer.style.opacity = 1;

  // Animate it.
  const self = this;
  // Second animation step.
  const animationSettings_2 = {
    complete() {
      self.isAnimating = false;
      if (typeof revealSettings.onComplete === 'function') {
        revealSettings.onComplete(self.content, self.revealer);
      }
    },
  };
  // First animation step.
  const animationSettings = {
    delay: revealSettings.delay || defaults.delay,
    complete() {
      self.revealer.style.WebkitTransformOrigin =
        self.revealer.style.transformOrigin = transformSettings.origin.halfway;
      if (typeof revealSettings.onCover === 'function') {
        revealSettings.onCover(self.content, self.revealer);
      }
      anime(animationSettings_2);
    },
  };

  animationSettings.targets = animationSettings_2.targets = this.revealer;
  animationSettings.duration = animationSettings_2.duration =
    revealSettings.duration || defaults.duration;
  animationSettings.easing = animationSettings_2.easing =
    revealSettings.easing || defaults.easing;

  const coverArea = revealSettings.coverArea || defaults.coverArea;
  if (direction === 'lr' || direction === 'rl') {
    animationSettings.scaleX = [0, 1];
    animationSettings_2.scaleX = [1, coverArea / 100];
  } else {
    animationSettings.scaleY = [0, 1];
    animationSettings_2.scaleY = [1, coverArea / 100];
  }

  if (typeof revealSettings.onStart === 'function') {
    revealSettings.onStart(self.content, self.revealer);
  }
  anime(animationSettings);
};

// ----------------------------------------------------------------------------
class Teaser extends React.Component {
  constructor(props) {
    super(props);
    this.handleEvent = this.handleEvent.bind(this);
  }

  componentDidMount() {
    const rev6 = new RevealFx(document.getElementById('rev-6'), {
      revealSettings: {
        bgcolor: this.props.imgRevOverlayColor,
        onCover(contentEl, revealerEl) {
          contentEl.style.opacity = 1;
        },
      },
    });
    const rev7 = new RevealFx(document.querySelector('#rev-7'), {
      revealSettings: {
        bgcolor: this.props.textFirstRevOverlayColor,
        direction: 'rl',
        delay: 250,
        onCover(contentEl, revealerEl) {
          contentEl.style.opacity = 1;
        },
      },
    });
    const rev8 = new RevealFx(document.querySelector('#rev-8'), {
      revealSettings: {
        bgcolor: this.props.textSecondRevOverlayColor,
        direction: 'rl',
        delay: 500,
        onCover(contentEl, revealerEl) {
          contentEl.style.opacity = 1;
        },
      },
    });
    const rev9 = this.props.linkHref
      ? new RevealFx(document.querySelector('#rev-9'), {
          revealSettings: {
            bgcolor: this.props.linkRevOverlayColor,
            direction: 'lr',
            delay: 750,
            onCover(contentEl, revealerEl) {
              contentEl.style.opacity = 1;
            },
          },
        })
      : null;

    setTimeout(function () {
      rev6.reveal();
      rev7.reveal();
      rev8.reveal();
      if (rev9) {
        rev9.reveal();
      }
    }, 1500);
  }

  handleEvent(e, target) {
    if (e.type === 'mousemove') {
      const targetX = e.x - target.offsetLeft;
      const targetY = e.y - target.offsetTop;
      const targetWidth = target.offsetWidth;
      const targetHeight = target.offsetHeight;
      document.getElementById(
        'text-animation-wrapper',
      ).style.left = `${-Math.round((targetX / targetWidth) * 50 - 25)}px`;
      document.getElementById(
        'text-animation-wrapper',
      ).style.top = `${-Math.round((targetY / targetHeight) * 50 - 25)}px`;
    } else if (e.type === 'mouseleave') {
      document.getElementById('text-animation-wrapper').style.left = 0;
      document.getElementById('text-animation-wrapper').style.top = 0;
    }
  }

  render() {
    return (
      <div className="content content--full flexy flexy--row">
        <div
          id="rev-6"
          className="content__image-wrap content__image-wrap--half"
        >
          <Link to={this.props.linkHref} title={this.props.linkTitle}>
            <Picture
              src={this.props.imgPath}
              className="content__image"
              alt={this.props.imgAlt}
              title={this.props.imgTitle}
            />
          </Link>
        </div>
        <Headline
          className={`content__title content__title--medium content__title--half content__title--left ${this.props.textColor}`}
        >
          <div id="text-animation-wrapper">
            <div
              id="rev-7"
              className={`content__title__inner ${
                this.props.textColorFirstLine && this.props.textColorFirstLine
              }`}
            >
              {this.props.textFirstLine}
            </div>
            <br />
            <div
              id="rev-8"
              className={`content__title__inner content__title__inner--offset-2 ${
                this.props.textColorSecondLine && this.props.textColorSecondLine
              }`}
            >
              {this.props.textSecondLine}
            </div>
          </div>
          {this.props.linkHref && (
            <div
              id="rev-9"
              className={`content__title__inner content__title__inner__link ${this.props.linkColorHover}`}
            >
              <Link
                to={this.props.linkHref}
                title={this.props.linkTitle}
                targetBlank={this.props.linkTargetBlank}
              >
                <Icon name={this.props.iconName} space />
                {this.props.linkTitle}
              </Link>
            </div>
          )}
        </Headline>
      </div>
    );
  }
}

Teaser.propTypes = {
  imgAlt: PropTypes.string.isRequired,
  iconName: PropTypes.string,
  linkColorHover: PropTypes.string,
  linkHref: PropTypes.string,
  linkTargetBlank: PropTypes.bool,
  linkTitle: PropTypes.string,
  linkRevOverlayColor: PropTypes.string,
  imgRevOverlayColor: PropTypes.string,
  imgPath: PropTypes.string.isRequired,
  textColor: PropTypes.oneOf(['gray', 'white', 'beige']),
  textFirstLine: PropTypes.string.isRequired,
  textColorFirstLine: PropTypes.string,
  textSecondLine: PropTypes.string.isRequired,
  textColorSecondLine: PropTypes.string,
  textFirstRevOverlayColor: PropTypes.string,
  textSecondRevOverlayColor: PropTypes.string,
  imgTitle: PropTypes.string.isRequired,
};

Teaser.defaultProps = {
  iconName: 'fa-arrow-circle-right',
  linkColorHover: 'gray',
  linkHref: null,
  linkTargetBlank: false,
  linkTitle: null,
  linkRevOverlayColor: '#44b479',
  imgRevOverlayColor: 'transparent',
  textColor: 'gray',
  textColorFirstLine: null,
  textColorSecondLine: null,
  textFirstRevOverlayColor: '#44b479',
  textSecondRevOverlayColor: '#44b479',
};

export default ReactEvent(['mousemove', 'mouseleave'])(Teaser);
